import {
  boxToPoints,
  rotatePoint,
  findCenterOfBox,
  offsetPointPairs,
} from "@logic/lib/2d"
import type { Panel } from "@logic/lib/design"
import type { Node2d, Edge2dComputed } from "@logic/lib/mesh"
import { atom, selector } from "recoil"

import { findPanelState, panelIndex, panelSelectionState } from "."

export const findSmallest = (...numbers: number[]): number => {
  return numbers.reduce((acc, n) => {
    if (Math.abs(n) < Math.abs(acc)) {
      acc = n
    }

    return acc
  }, Number.MAX_SAFE_INTEGER)
}

export const SNAP_THRESHOLD = 5

export const panelSelectionSnapPointsState = selector<Node2d[]>({
  key: `panelSelectionSnapPoints`,
  get: ({ get }) => {
    const selection = get(panelSelectionState)
    const panels = [...selection].map((panelId) => get(findPanelState(panelId)))
    const origin = panels[0]

    if (!origin) {
      return []
    }

    const { min, max } = panels.reduce(
      (acc, panel) => {
        const points = boxToPoints(panel).map((p) =>
          rotatePoint(origin, p, -origin.r),
        )

        return {
          min: {
            x: Math.min(
              acc.min.x,
              points[0].x,
              points[1].x,
              points[2].x,
              points[3].x,
            ),
            y: Math.min(
              acc.min.y,
              points[0].y,
              points[1].y,
              points[2].y,
              points[3].y,
            ),
          },
          max: {
            x: Math.max(
              acc.max.x,
              points[0].x,
              points[1].x,
              points[2].x,
              points[3].x,
            ),
            y: Math.max(
              acc.max.y,
              points[0].y,
              points[1].y,
              points[2].y,
              points[3].y,
            ),
          },
        }
      },
      {
        min: { x: Infinity, y: Infinity },
        max: { x: 0, y: 0 },
      },
    )
    const extraPoints =
      origin.r === 0 ? [{ x: (min.x + max.x) / 2, y: (min.y + max.y) / 2 }] : []

    return [
      { x: min.x, y: min.y },
      { x: min.x, y: max.y },
      { x: max.x, y: min.y },
      { x: max.x, y: max.y },
      ...extraPoints,
    ].map((p) => rotatePoint(origin, p, origin.r))
  },
})

// group A: snap points for selection
// group B: snap points for unselected panels with same rotation
// find the pair of points (A B) with the smallest y-distance
// find the pair of points (A B) with the smallest x-distance

const unselectedPanelsWithSameRotationState = selector<Panel[]>({
  key: `unselectedPanelsWithSameRotation`,
  get: ({ get }) => {
    const selection = get(panelSelectionState)
    const panelIds = get(panelIndex)
    const unselectedPanelIds = panelIds.filter(
      (panelId) => !selection.has(panelId),
    )
    const panel = get(findPanelState([...selection][0]))
    const { r } = panel

    return unselectedPanelIds
      .map((panelId) => {
        const currentPanel = get(findPanelState(panelId))

        return {
          ...currentPanel,
          r: currentPanel ? currentPanel.r - (currentPanel.orientation ?? 0) : 0,
        }
      })
      .filter((panel) => panel.r - r < 0.0001)
  },
})

export const unselectedPanelPointsState = selector<Node2d[][]>({
  key: `unselectedPanelPoints`,
  get: ({ get }) => {
    const panelsNoOrientation = get(unselectedPanelsWithSameRotationState)
    const panels = panelsNoOrientation.map((panel) => {
      return { ...panel, r: panel.r + (panel?.orientation ?? 0) }
    })

    return panels[0]
      ? panels[0].r === 0
        ? panels.map((panel) => [...boxToPoints(panel), findCenterOfBox(panel)])
        : panels.map((panel) => [...boxToPoints(panel)])
      : []
  },
})

export const isSnappingOnState = atom({
  key: `isSnappingOn`,
  default: true,
})

export const panelSnapTranslocationState = selector<{
  shouldSnap: { x: boolean; y: boolean }
  offset: Node2d
  pointPairs: { x?: Edge2dComputed; y?: Edge2dComputed }
}>({
  key: `panelSnapTranslocation`,
  get: ({ get }) => {
    const isSnappingOn = get(isSnappingOnState)
    if (!isSnappingOn) {
      return {
        shouldSnap: { x: false, y: false },
        offset: { x: 0, y: 0 },
        pointPairs: {},
      }
    }
    const unselectedPanelsWithSameRotation = get(
      unselectedPanelsWithSameRotationState,
    )
    const unselectedPanelPoints = get(unselectedPanelPointsState)
    if (unselectedPanelPoints.length === 0) {
      return {
        shouldSnap: { x: false, y: false },
        offset: { x: 0, y: 0 },
        pointPairs: {},
      }
    }

    const origin = unselectedPanelsWithSameRotation[0]

    const selectionSnapPoints = get(panelSelectionSnapPointsState)
    // .map((p) =>
    //   rotatePoint(origin, p, origin?.r ?? 0),
    // )

    if (origin.r === 0) {
      const { offset, pointPairs } = unselectedPanelPoints.reduce<{
        offset: Node2d
        pointPairs: { x?: Edge2dComputed; y?: Edge2dComputed }
      }>(
        (acc, panelSnapPoints) => {
          const nodePairs = offsetPointPairs(
            panelSnapPoints,
            selectionSnapPoints,
          )

          const horizontalOffsets = nodePairs.map(({ offset }) => offset.x)
          const verticalOffsets = nodePairs.map(({ offset }) => offset.y)

          const ySmallestOffset = findSmallest(...verticalOffsets)
          const xSmallestOffset = findSmallest(...horizontalOffsets)

          if (Math.abs(xSmallestOffset) < Math.abs(acc.offset.x)) {
            acc.offset.x = xSmallestOffset
            acc.pointPairs.x = nodePairs.find(
              ({ offset }) => offset.x === xSmallestOffset,
            )?.pair
          }
          if (Math.abs(ySmallestOffset) < Math.abs(acc.offset.y)) {
            acc.offset.y = ySmallestOffset
            acc.pointPairs.y = nodePairs.find(
              ({ offset }) => offset.y === ySmallestOffset,
            )?.pair
          }

          return acc
        },
        {
          offset: { x: Infinity, y: Infinity },
          pointPairs: {
            x: {
              p1: { x: Infinity, y: Infinity },
              p2: { x: Infinity, y: Infinity },
            },
            y: {
              p1: { x: Infinity, y: Infinity },
              p2: { x: Infinity, y: Infinity },
            },
          },
        },
      )

      return {
        shouldSnap: {
          x: Math.abs(offset.x) < SNAP_THRESHOLD,
          y: Math.abs(offset.y) < SNAP_THRESHOLD,
        },
        offset,
        pointPairs,
      }
    }
    const { offset, pointPairs } = unselectedPanelPoints.reduce<{
      offset: Node2d
      pointPairs: { x?: Edge2dComputed; y?: Edge2dComputed }
    }>(
      (acc, panelSnapPoints) => {
        const nodePairs = offsetPointPairs(panelSnapPoints, selectionSnapPoints)
        for (const { offset, pair } of nodePairs) {
          if (
            Math.abs(offset.x) + Math.abs(offset.y) <
            Math.abs(acc.offset.x) + Math.abs(acc.offset.y)
          ) {
            acc.offset.x = offset.x
            acc.offset.y = offset.y
            acc.pointPairs.x = pair
            acc.pointPairs.y = pair
          }
        }

        return acc
      },
      {
        offset: { x: Infinity, y: Infinity },
        pointPairs: {
          x: {
            p1: { x: Infinity, y: Infinity },
            p2: { x: Infinity, y: Infinity },
          },
          y: {
            p1: { x: Infinity, y: Infinity },
            p2: { x: Infinity, y: Infinity },
          },
        },
      },
    )
    const shouldSnap =
      Math.abs(offset.x) < SNAP_THRESHOLD && Math.abs(offset.y) < SNAP_THRESHOLD

    return {
      shouldSnap: {
        x: shouldSnap,
        y: shouldSnap,
      },
      offset,
      pointPairs,
    }
  },
})
